import { scalpingImpact } from '../data';

interface ModeProfile {
  key: 'scalper' | 'dayTrader' | 'swingTrader';
  label: string;
  tagline: string;
  severity: 'high' | 'medium' | 'low';
}

const profiles: ModeProfile[] = [
  {
    key: 'scalper',
    label: 'Scalper',
    tagline: '15 trades/day, 8-pip target, 0.1 lots',
    severity: 'high',
  },
  {
    key: 'dayTrader',
    label: 'Day Trader',
    tagline: '5 trades/day, 25-pip target, 0.5 lots',
    severity: 'medium',
  },
  {
    key: 'swingTrader',
    label: 'Swing Trader',
    tagline: '1 trade/day, 120-pip target, 2 lots',
    severity: 'low',
  },
];

function computeModeStats(key: 'scalper' | 'dayTrader' | 'swingTrader') {
  const d = scalpingImpact[key];
  const pipValue = 10;
  const spreadPerTrade = d.spread * pipValue * d.lots;
  const tradesPerMonth = d.tradesPerDay * d.days;
  const monthlySpread = spreadPerTrade * tradesPerMonth;
  const monthlyGross = d.pipsPerTrade * pipValue * d.lots * tradesPerMonth;
  const ratio = (monthlySpread / monthlyGross) * 100;
  const monthlyNet = monthlyGross - monthlySpread;

  return {
    tradesPerMonth,
    spreadPerTrade,
    monthlySpread,
    monthlyGross,
    ratio,
    monthlyNet,
  };
}

const severityColors: Record<string, { bar: string; bg: string; text: string; badge: string }> = {
  high: {
    bar: 'bg-accent-500',
    bg: 'bg-accent-100',
    text: 'text-accent-700',
    badge: 'bg-accent-100 text-accent-700',
  },
  medium: {
    bar: 'bg-primary-500',
    bg: 'bg-primary-100',
    text: 'text-primary-700',
    badge: 'bg-primary-100 text-primary-700',
  },
  low: {
    bar: 'bg-secondary-500',
    bg: 'bg-secondary-100',
    text: 'text-secondary-700',
    badge: 'bg-secondary-100 text-secondary-700',
  },
};

export default function SpreadImpactSection() {
  return (
    <section id="spread-impact" className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="max-w-[680px] mb-12 md:mb-16">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.12] text-foreground-950 tracking-tight mb-4">
            Spread eats your edge.
            <br />
            The shorter your target, the bigger the bite.
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            A 1.2 pip spread is nothing on a 120-pip swing — it is 1% of the target. But on an 8-pip scalp, that same spread
            consumes 15% of your entire profit before the market even moves. Three traders, same spread, wildly different outcomes.
          </p>
        </div>

        {/* Three mode cards — side by side comparison */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-10 md:mb-12">
          {profiles.map((profile) => {
            const stats = computeModeStats(profile.key);
            const colors = severityColors[profile.severity];
            const barWidth = Math.max(stats.ratio, 3);

            return (
              <div
                key={profile.key}
                className="bg-white rounded-card border border-background-200 p-6 md:p-7 flex flex-col hover:border-background-300 transition-colors duration-200 group"
              >
                {/* Card header */}
                <div className="mb-5">
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <span className={`text-[13px] font-bold ${colors.text} uppercase tracking-wide`}>
                      {profile.label}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${colors.badge}`}>
                      {profile.severity === 'high' ? 'High impact' : profile.severity === 'medium' ? 'Moderate' : 'Minimal'}
                    </span>
                  </div>
                  <p className="text-[13px] text-foreground-500 leading-relaxed">{profile.tagline}</p>
                </div>

                {/* Ratio — the hero number */}
                <div className="mb-4">
                  <div className="text-[13px] font-medium text-foreground-500 mb-1.5">
                    Spread as % of profit target
                  </div>
                  <div className={`text-[48px] md:text-[56px] font-[800] leading-none tracking-tighter ${colors.text}`}>
                    {stats.ratio.toFixed(1)}%
                  </div>
                </div>

                {/* Visual bar: spread eats profit */}
                <div className="mb-5">
                  <div className="relative h-[32px] bg-background-100 rounded-full overflow-hidden">
                    <div
                      className={`absolute left-0 top-0 h-full ${colors.bar} rounded-full transition-all duration-700 ease-out group-hover:brightness-110`}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-[3px] ${colors.bar} flex-shrink-0`} />
                      <span className="text-[12px] text-foreground-500">Spread cost</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-[3px] bg-background-200 flex-shrink-0" />
                      <span className="text-[12px] text-foreground-500">Your profit</span>
                    </div>
                  </div>
                </div>

                {/* Net & spread numbers */}
                <div className="bg-background-50 rounded-[10px] p-4 mb-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] text-foreground-600">Net profit / month</span>
                    <span className="text-[17px] font-[750] text-foreground-950">
                      ${stats.monthlyNet.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] text-foreground-600">Lost to spread</span>
                    <span className={`text-[15px] font-bold ${colors.text}`}>
                      −${stats.monthlySpread.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  <div className="border-t border-background-200 pt-2.5 flex items-center justify-between">
                    <span className="text-[13px] text-foreground-600">Gross profit</span>
                    <span className="text-[14px] font-semibold text-foreground-700">
                      ${stats.monthlyGross.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </span>
                  </div>
                </div>

                {/* Bottom stats row */}
                <div className="mt-auto pt-4 border-t border-background-200 grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[12px] text-foreground-500 mb-0.5">Trades/month</div>
                    <div className="text-[16px] font-bold text-foreground-800">{stats.tradesPerMonth}</div>
                  </div>
                  <div>
                    <div className="text-[12px] text-foreground-500 mb-0.5">Cost/trade</div>
                    <div className="text-[16px] font-bold text-foreground-800">${stats.spreadPerTrade.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom insight callout */}
        <div className="bg-foreground-950 rounded-card p-6 md:p-8">
          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            {/* Left: insight */}
            <div className="flex-1 max-w-[520px]">
              <div className="text-[12px] font-semibold text-foreground-500 uppercase tracking-wide mb-3">
                The real takeaway
              </div>
              <h4 className="text-[20px] md:text-[24px] font-[700] text-foreground-100 leading-snug mb-3">
                Spread does not scale with ambition — it stays fixed while your target grows
              </h4>
              <p className="text-[14px] md:text-[15px] text-foreground-400 leading-relaxed">
                A scalper targeting 8 pips loses 15% to spread. A day trader targeting 25 pips loses under 5%. A swing trader targeting
                120 pips loses barely 1%. The same 1.2 pip spread. The lesson is not to stop scalping — it is to time your entries
                when spreads are tightest and never scalp during news or session transitions.
              </p>
            </div>

            {/* Right: comparison strip */}
            <div className="flex-1 flex flex-col justify-center gap-4">
              {profiles.map((profile) => {
                const stats = computeModeStats(profile.key);
                const colors = severityColors[profile.severity];
                const miniBarWidth = Math.max(stats.ratio, 2);

                return (
                  <div key={profile.key} className="flex items-center gap-3">
                    <span className="text-[13px] font-semibold text-foreground-300 w-[80px] flex-shrink-0 text-right">
                      {profile.label}
                    </span>
                    <div className="flex-1 h-[10px] bg-foreground-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${colors.bar} rounded-full`}
                        style={{ width: `${miniBarWidth}%` }}
                      />
                    </div>
                    <span className={`text-[13px] font-bold w-[48px] flex-shrink-0 ${colors.text}`}>
                      {stats.ratio.toFixed(1)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Divider + note */}
          <div className="mt-6 pt-6 border-t border-foreground-800">
            <div className="flex items-start gap-2.5">
              <i className="ri-information-line text-[16px] text-foreground-500 mt-0.5 w-5 h-5 flex items-center justify-center flex-shrink-0" />
              <p className="text-[13px] text-foreground-500 leading-relaxed">
                All calculations assume a 1.2 pip spread on a standard account with $10/pip value. At 3 pips during volatile
                sessions, the scalper ratio jumps to 37.5% — turning a winning strategy into a break-even one. Choose your
                trading hours as carefully as your entry signal.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}